import React from 'react';
import { Button, Box, Card, CardHeader, CardContent, Stack, Typography, IconButton } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitPurchasersAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { PiggyBankSection } from './PiggyBankSection';

export const PurchaserForm = props => {
  const { data, submitCallback, handleClose } = props;

  const { loading, mutate } = useMutation(submitPurchasersAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    street_address: Yup.string().max(200, 'Adres może mięc najwyżej 200 znaków'),
    postal_code: Yup.string().max(20, 'Kod pocztowy może mięc najwyżej 20 znaków'),
    city: Yup.string().max(50, 'Miasto może mięc najwyżej 50 znaków'),
    nip: Yup.string().max(15, 'NIP może mięc najwyżej 15 znaków'),
    phone: Yup.string().max(12, 'Numer telefonu może mięc najwyżej 12 znaków'),
    correspondence_email: Yup.string().email(),
    birth_date: Yup.date().nullable()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    street_address: data ? data.street_address || '' : '',
    postal_code: data ? data.postal_code || '' : '',
    city: data ? data.city || '' : '',
    nip: data ? data.nip || '' : '',
    phone: data ? data.phone || '' : '',
    correspondence_email: data ? data.correspondence_email || '' : '',
    type: data ? data.type || 'guardian' : 'guardian'
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano dane zamawiającego' : 'Dodano nowego zamawiającego', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego zamawiającego');
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto zamawiającego ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć zamawiającego ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Card>
        <CardHeader title="Dane zamawiającego" sx={{ mt: -1, mb: -2 }} />
        <CardContent>
          <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
            <Box display="flex" flexDirection="row" mt={1}>
              <Stack spacing={1} flex="1 1 50%">
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Imię i nazwisko</Typography>
                  <DebounceInput
                    name="name"
                    id="name"
                    type="text"
                    placeholder="imię i nazwisko"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                    autoFocus
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Adres</Typography>
                  <DebounceInput
                    name="street_address"
                    id="street_address"
                    type="text"
                    placeholder="ulica"
                    value={formik.values.street_address}
                    onChange={formik.handleChange}
                    error={formik.touched.street_address && Boolean(formik.errors.street_address)}
                    helperText={formik.touched.street_address && formik.errors.street_address}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Kod pocztowy</Typography>
                  <DebounceInput
                    name="postal_code"
                    id="postal_code"
                    type="text"
                    placeholder="kod pocztowy"
                    value={formik.values.postal_code}
                    onChange={formik.handleChange}
                    error={formik.touched.postal_code && Boolean(formik.errors.postal_code)}
                    helperText={formik.touched.postal_code && formik.errors.postal_code}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Miejscowość</Typography>
                  <DebounceInput
                    name="city"
                    id="city"
                    type="text"
                    placeholder="miasto"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
              </Stack>

              <Stack spacing={1} flex="1 1 50%" mr="15%">
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">NIP</Typography>
                  <DebounceInput
                    name="nip"
                    id="nip"
                    type="text"
                    placeholder="nip"
                    value={formik.values.nip}
                    onChange={formik.handleChange}
                    error={formik.touched.nip && Boolean(formik.errors.nip)}
                    helperText={formik.touched.nip && formik.errors.nip}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Telefon</Typography>
                  <DebounceInput
                    name="phone"
                    id="phone"
                    type="text"
                    placeholder="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">E-mail</Typography>
                  <DebounceInput
                    name="correspondence_email"
                    id="correspondence_email"
                    type="text"
                    placeholder="e-mail"
                    value={formik.values.correspondence_email}
                    onChange={formik.handleChange}
                    error={formik.touched.correspondence_email && Boolean(formik.errors.correspondence_email)}
                    helperText={formik.touched.correspondence_email && formik.errors.correspondence_email}
                    debounceTimeout={100}
                    element={SmallFormTextField}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
                  <Typography variant="subtitle2">Rodzaj</Typography>
                  <SmallFormTextField
                    select
                    id="type"
                    name="type"
                    options={[{ value: 'guardian', label: 'Opiekun' }, { value: 'contractor', label: 'Kontrahent' }]}
                    value={formik.values.type}
                    onChange={formik.handleChange} />
                </Stack>
              </Stack>
            </Box>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              <AddIcon />
              Zapisz
            </Button>
            { data
              ? <Button
                color="error"
                variant="contained"
                sx={{ mt: 3, ml: 4 }}
                onClick={() => {
                  setConfirmDeleteOpen(true);
                }}
              >
                <DeleteIcon />
                Usuń
              </Button>
              : null}
          </form>
        </CardContent>
      </Card>
      <ConfirmDialog
        title="Usunąć Zamawiającego?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć tego zamawiającego?
      </ConfirmDialog>
      {data && <PiggyBankSection purchaserId={data.id} />}
    </>

  );
};
